import { lazy, Suspense } from 'react';

import { Outlet, Navigate, useRoutes, useLocation } from 'react-router-dom';

// guards
import AuthGuard from '../guards/AuthGuard';
import GuestGuard from '../guards/GuestGuard';
// layouts
import DashboardLayout from '../layouts/dashboard';
import RoleBasedGuard from '../guards/RoleBasedGuard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// components
import LoadingScreen from '../components/LoadingScreen';
import LoginCallbackLayout from '../layouts/LoginCallbackLayout';

// ----------------------------------------------------------------------

const Loadable = (Component) =>
  function (props) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { pathname } = useLocation();
    const isDashboard = pathname.includes('/dashboard');

    return (
      <Suspense
        fallback={
          <LoadingScreen
            sx={{
              ...(!isDashboard && {
                top: 0,
                left: 0,
                width: 1,
                zIndex: 9999,
                backgroundColor: '#9f9f9f54',
                position: 'fixed'
              })
            }}
          />
        }
      >
        <Component {...props} />
      </Suspense>
    );
  };

export default function Router() {
  return useRoutes([
    {
      path: 'confirm-doc-email/:id',
      element: <DocumentEmailConfirm />
    },
    {
      path: 'latecharge/mail-approve/:id/:emailid/:actionid/:time',
      element: <LateChargeApprove />
    },
    {
      path: 'latecharge/mail-last-approve/:id/:emailid/:actionid/:time',
      element: <LateChargeLastApprove />
    },
    // Main Routes
    {
      path: '/',
      element: (
        <AuthGuard>
          <LandingPage />
        </AuthGuard>
      )
    },
    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <DashboardLayout>
          <AuthGuard>
            <Outlet />
          </AuthGuard>
        </DashboardLayout>
      ),
      children: [
        { path: '/', element: <Navigate to="/dashboard/master/" replace /> },
        {
          path: 'master',
          element: (
            <RoleBasedGuard accessibleRoles={['Credit']}>
              <Outlet />
            </RoleBasedGuard>
          ),
          children: [
            { path: '/', element: <Navigate to="/dashboard/master/configuration" replace /> },
            { path: 'configuration', element: <Configuration /> },
            { path: 'cutoff-document', element: <CutoffDocument /> },
            { path: 'customer-behavior', element: <CustomerBehavior /> },
            { path: 'risk-category', element: <RiskCategory /> },
            { path: 'bill-collector', element: <CreditRepGroup /> },
            { path: 'payment-term', element: <CustCredGroup /> },
            { path: 'customer-bank', element: <CustomersBank /> },
            { path: 'payment-bank', element: <PaymentBank /> },
            { path: 'incomming-gl', element: <IncommingGL /> },
            { path: 'credit-team', element: <CreditTeam /> },
            { path: 'team-mapping', element: <CreditTeamMapping /> },
            { path: 'trade-type', element: <TradeType /> },
            { path: 'bill-method', element: <BillMethod /> },
            { path: 'payment-type', element: <PaymentType /> },
            { path: 'scg-holiday', element: <SCGHoliday /> },
            { path: 'bank-holiday', element: <BankHoliday /> },
            { path: 'main-sales-emp', element: <MainSalesEmp /> },
            { path: 'email-group', element: <EmailGroup /> },
            { path: 'config-email', element: <ConfigEmail /> },
            { path: 'main-sales-group-sales-office', element: <MainSalesGroupOffice /> },
            { path: 'select-in-select-out', element: <SelectInSelectOut /> },
            { path: 'insurance-company', element: <InsuranceCompany /> },
            { path: 'snap-credit-master', element: <SnapCreditMaster /> },
            { path: 'users', element: <Users /> },
            { path: 'role', element: <Role /> }
          ]
        },
        {
          path: 'invoice',
          element: (
            <RoleBasedGuard accessibleRoles={['Credit', 'WH', 'Logistic']}>
              <Outlet />
            </RoleBasedGuard>
          ),
          children: [
            { path: '/', element: <Navigate to="/dashboard/invoice/bill" replace /> },
            {
              path: 'following',
              element: (
                <RoleBasedGuard accessibleRoles={['Credit']}>
                  <Invoice />
                </RoleBasedGuard>
              )
            },
            {
              path: 'bill',
              element: <Bill />
            },
            {
              path: 'po',
              element: (
                <RoleBasedGuard accessibleRoles={['Credit']}>
                  <Po />
                </RoleBasedGuard>
              )
            },
            {
              path: 'outsource',
              element: (
                <RoleBasedGuard accessibleRoles={['Credit']}>
                  <Outsource />
                </RoleBasedGuard>
              )
            },
            {
              path: 'newOutsource',
              element: (
                <RoleBasedGuard accessibleRoles={['Credit']}>
                  <NewOutsource />
                </RoleBasedGuard>
              )
            },
            {
              path: 'calling-daily',
              element: (
                <RoleBasedGuard accessibleRoles={['Credit']}>
                  <CallingDaily />
                </RoleBasedGuard>
              )
            },
            {
              path: 'bu-messenger',
              element: (
                <RoleBasedGuard accessibleRoles={['Credit']}>
                  <BuMessenger />
                </RoleBasedGuard>
              )
            },
            {
              path: 'e-mail',
              element: (
                <RoleBasedGuard accessibleRoles={['Credit']}>
                  <Email />
                </RoleBasedGuard>
              )
            },
            {
              path: 'ems',
              element: (
                <RoleBasedGuard accessibleRoles={['Credit']}>
                  <Ems />
                </RoleBasedGuard>
              )
            },
            {
              path: 'customer-system',
              element: (
                <RoleBasedGuard accessibleRoles={['Credit']}>
                  <CustomerSystem />
                </RoleBasedGuard>
              )
            },
            {
              path: 'daily-file/:id',
              element: (
                <RoleBasedGuard accessibleRoles={['Credit']}>
                  <DailyFile />
                </RoleBasedGuard>
              )
            },
            {
              path: 'plan-bill/:id',
              element: (
                <RoleBasedGuard accessibleRoles={['Credit']}>
                  <PlanBill />
                </RoleBasedGuard>
              )
            },
            {
              path: 'calling/:id',
              element: (
                <RoleBasedGuard accessibleRoles={['Credit']}>
                  <Calling />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'document-management',
          element: (
            <RoleBasedGuard accessibleRoles={['Credit', 'Sales']}>
              <Outlet />
            </RoleBasedGuard>
          ),
          children: [
            {
              path: 'billing-receipt/*',
              element: (
                <RoleBasedGuard accessibleRoles={['Credit']}>
                  <BillingReceipt />
                </RoleBasedGuard>
              )
            },
            {
              path: 'document-invoice/*',
              element: (
                <RoleBasedGuard accessibleRoles={['Credit']}>
                  <DocumentInvoice />
                </RoleBasedGuard>
              )
            },
            { path: 'ticket-be', element: <TicketBE /> },
            { path: 'ticket-dlc', element: <TicketDLC /> }
          ]
        },
        {
          path: 'payment-management',
          element: (
            <RoleBasedGuard accessibleRoles={['Credit', 'Sales']}>
              <Outlet />
            </RoleBasedGuard>
          ),
          children: [
            {
              path: 'gl-statemant',
              element: (
                <RoleBasedGuard accessibleRoles={['Credit']}>
                  <GLStatemant />
                </RoleBasedGuard>
              )
            },
            {
              path: 'following-payment',
              element: (
                <RoleBasedGuard accessibleRoles={['Credit']}>
                  <FollowingPayment />
                </RoleBasedGuard>
              )
            },
            {
              path: 'detail-following-payment',
              element: (
                <RoleBasedGuard accessibleRoles={['Credit']}>
                  <DetailFollowingPayment />
                </RoleBasedGuard>
              )
            },
            {
              path: 'incoming-payment/*',
              element: (
                <RoleBasedGuard accessibleRoles={['Credit']}>
                  <IncomingPayment />
                </RoleBasedGuard>
              )
            },
            {
              path: 'detail-incoming-payment',
              element: (
                <RoleBasedGuard accessibleRoles={['Credit']}>
                  <DetailIncomingPayment />
                </RoleBasedGuard>
              )
            },
            {
              path: 'detail-manully-matching',
              element: (
                <RoleBasedGuard accessibleRoles={['Credit']}>
                  <DetailManullyMatching />
                </RoleBasedGuard>
              )
            },
            {
              path: 'search-problem/*',
              element: (
                <RoleBasedGuard accessibleRoles={['Credit']}>
                  <SearchProblem />
                </RoleBasedGuard>
              )
            },
            {
              path: 'detail-search-problem',
              element: (
                <RoleBasedGuard accessibleRoles={['Credit']}>
                  <DetailSearchProblem />
                </RoleBasedGuard>
              )
            },
            { path: 'cheque-return', element: <ChequeReturn /> },
            { path: 'create-cheque-return', element: <CreateChequeReturn /> },
            { path: 'update-cheque-return/:id', element: <CreateChequeReturn /> }
          ]
        },
        {
          path: 'work-flow',
          element: (
            <RoleBasedGuard accessibleRoles={['Credit', 'Sales']}>
              <Outlet />
            </RoleBasedGuard>
          ),
          children: [
            { path: 'late-charge/*', element: <LateCharge /> },
            { path: 'detail-late-charge-create', element: <DetailLateChargeCreate /> },
            { path: 'detail-late-charge-other', element: <DetailLateChargeOther /> },
            { path: 'postpone-cheque', element: <PostponeCheque /> },
            { path: 'suspend-list', element: <UnblockOrder /> },
            { path: 'suspend', element: <UnblockOrderForm /> },
            { path: 'suspend/:id/:action', element: <UnblockOrderForm /> },
            { path: 'suspend/:id', element: <UnblockOrderForm /> },
            { path: 'questionnaires', element: <Questionnaires /> }
          ]
        },
        {
          path: 'control-management',
          element: (
            <RoleBasedGuard accessibleRoles={['Credit']}>
              <Outlet />
            </RoleBasedGuard>
          ),
          children: [
            { path: 'highlight-overdue', element: <HighlightOverdue /> },
            { path: 'highlight-overdue-detail/*', element: <HighlightOverdueDetail /> }
          ]
        },
        {
          path: 'risk-management',
          element: (
            <RoleBasedGuard accessibleRoles={['Credit']}>
              <Outlet />
            </RoleBasedGuard>
          ),
          children: [
            { path: '/', element: <Navigate to="/dashboard/risk-management/debenture" replace /> },
            { path: 'debenture', element: <Debenture /> },
            { path: 'insurance/*', element: <Insurance /> }
          ]
        },
        {
          path: 'report',
          element: (
            <RoleBasedGuard accessibleRoles={['Credit']}>
              <Outlet />
            </RoleBasedGuard>
          ),
          children: [
            { path: 'plan-cash-in-flow', element: <PlanCashInFlow /> },
            { path: 'overdue-report', element: <OverdueReport /> },
            { path: 'late-charge-report', element: <MonthlyReport /> },
            { path: 'cheque-return-report', element: <ChequeReturnReport /> },
            { path: 'sales-volume-by-credit-term', element: <SalesVolumeByCreditTerm /> },
            { path: 'non-sap', element: <NonSap /> },
            { path: 'kpi-overdue', element: <KPIOverdue /> },
            { path: 'committee', element: <Committee /> }
          ]
        },
        {
          path: 'export-payment',
          element: (
            <RoleBasedGuard accessibleRoles={['Credit']}>
              <Outlet />
            </RoleBasedGuard>
          ),
          children: [
            { path: 'export-following-payment', element: <FollowingPaymentExport /> },
            { path: 'export-search-problem/*', element: <SearchProblemExport /> },
            { path: 'export-detail-search-problem', element: <DetailSearchProblemExport /> },
            { path: 'export-overdue-report', element: <OverdueReportExport /> },
            { path: 'export-incoming-payment', element: <IncomingPaymentExport /> },
            { path: 'export-credit-advice', element: <CreditAdvice /> },
            {
              path: 'export-incoming-payment-manual',
              children: [
                {
                  path: ':id',
                  element: <IncomingExportPaymentManualDetail />
                },
                {
                  path: 'create/:id',
                  element: <IncomingExportPaymentManualCreate />
                }
              ]
            }
          ]
        }
      ]
    },

    // Login Callback Routes
    {
      path: 'auth',
      element: (
        <GuestGuard>
          <LoginCallbackLayout />
        </GuestGuard>
      ),
      children: [
        { path: '/', element: <Navigate to="/dashboard" replace /> },
        { path: 'login-cb', element: <LoginRedirectCallback /> },
        { path: 'login-silent-cb', element: <LoginSilentCallback /> },
        { path: 'login-popup-cb', element: <LoginPopupCallback /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    },

    // Logout
    { path: 'logout-front-channel', element: <LogoutFrontChannel /> },

    // Notfound
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    }
  ]);
}

// IMPORT COMPONENTS
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
// Invoice
const Bill = Loadable(lazy(() => import('../pages/InvoiceManagement/Invoice')));
const Po = Loadable(lazy(() => import('../pages/InvoiceManagement/FollowingPo')));
const Invoice = Loadable(lazy(() => import('../pages/InvoiceManagement/FollowingInvoice')));
const Outsource = Loadable(lazy(() => import('../pages/InvoiceManagement/Outsource')));
const NewOutsource = Loadable(lazy(() => import('../pages/InvoiceManagement/NewOutsource')));
const CallingDaily = Loadable(lazy(() => import('../pages/InvoiceManagement/CallingDaily')));
const BuMessenger = Loadable(lazy(() => import('../pages/InvoiceManagement/BuMessenger')));
const Email = Loadable(lazy(() => import('../pages/InvoiceManagement/Email')));
const Ems = Loadable(lazy(() => import('../pages/InvoiceManagement/Ems')));
const DailyFile = Loadable(lazy(() => import('../pages/InvoiceManagement/DailyFile')));
const PlanBill = Loadable(lazy(() => import('../pages/InvoiceManagement/PlanBill')));
const Calling = Loadable(lazy(() => import('../pages/InvoiceManagement/Calling')));
const DocumentEmailConfirm = Loadable(lazy(() => import('../pages/InvoiceManagement/DocumentEmailConfirm')));
const CustomerSystem = Loadable(lazy(() => import('../pages/InvoiceManagement/CustomerSystem')));

// Master
const Configuration = Loadable(lazy(() => import('../pages/Master/Configuration')));
const CustomerBehavior = Loadable(lazy(() => import('../pages/Master/CustomerBehavior')));
const CutoffDocument = Loadable(lazy(() => import('../pages/Master/Cutoffdocument')));
const RiskCategory = Loadable(lazy(() => import('../pages/Master/RiskCategory')));
const CreditRepGroup = Loadable(lazy(() => import('../pages/Master/CreditRepGroup')));
const CustCredGroup = Loadable(lazy(() => import('../pages/Master/CustCredGroup')));
const CustomersBank = Loadable(lazy(() => import('../pages/Master/CustomersBank')));
const PaymentBank = Loadable(lazy(() => import('../pages/Master/PaymentBank')));
const IncommingGL = Loadable(lazy(() => import('../pages/Master/IncommingGL')));
const CreditTeam = Loadable(lazy(() => import('../pages/Master/CreditTeam')));
const CreditTeamMapping = Loadable(lazy(() => import('../pages/Master/CreditTeamMapping')));
const TradeType = Loadable(lazy(() => import('../pages/Master/TradeType')));
const BillMethod = Loadable(lazy(() => import('../pages/Master/BillMethod')));
const PaymentType = Loadable(lazy(() => import('../pages/Master/PaymentType')));
const SCGHoliday = Loadable(lazy(() => import('../pages/Master/SCGHoliday')));
const BankHoliday = Loadable(lazy(() => import('../pages/Master/BankHoliday')));
const MainSalesEmp = Loadable(lazy(() => import('../pages/Master/MainSalesEmp')));
const EmailGroup = Loadable(lazy(() => import('../pages/Master/EmailGroup')));
const ConfigEmail = Loadable(lazy(() => import('../pages/Master/ConfigEmail')));
const MainSalesGroupOffice = Loadable(lazy(() => import('../pages/Master/MainSalesGroupOffice')));
const SelectInSelectOut = Loadable(lazy(() => import('../pages/Master/SelectInSelectOut')));
const InsuranceCompany = Loadable(lazy(() => import('../pages/Master/InsuranceCompany')));
const SnapCreditMaster = Loadable(lazy(() => import('../pages/Master/SnapCreditMaster')));
const Users = Loadable(lazy(() => import('../pages/Master/Users')));
const Role = Loadable(lazy(() => import('../pages/Master/Role')));

// Control Management
const HighlightOverdue = Loadable(lazy(() => import('../pages/ControlManagement/HighlightOverdue')));
const HighlightOverdueDetail = Loadable(lazy(() => import('../pages/ControlManagement/HighlightOverdueDetail')));
const Debenture = Loadable(lazy(() => import('../pages/RiskManagement/Debenture')));
const Insurance = Loadable(lazy(() => import('../pages/RiskManagement/Insurance')));

// PaymentManagement
const PostponeCheque = Loadable(lazy(() => import('../pages/PaymentManagement/PostponeCheque')));
const UnblockOrder = Loadable(lazy(() => import('../pages/WorkFlow/UnblockOrder/UnblockOrder')));
const UnblockOrderForm = Loadable(lazy(() => import('../pages/WorkFlow/UnblockOrder/UnblockOrderForm')));
const ChequeReturn = Loadable(lazy(() => import('../pages/WorkFlow/ChequeReturn')));
const CreateChequeReturn = Loadable(lazy(() => import('../pages/WorkFlow/CreateChequeReturn')));
const GLStatemant = Loadable(lazy(() => import('../pages/PaymentManagement/GLStatemant')));
const FollowingPayment = Loadable(lazy(() => import('../pages/PaymentManagement/FollowingPayment')));
const DetailFollowingPayment = Loadable(lazy(() => import('../pages/PaymentManagement/DetailFollowingPayment')));
const IncomingPayment = Loadable(lazy(() => import('../pages/PaymentManagement/IncomingPayment')));
const DetailIncomingPayment = Loadable(lazy(() => import('../pages/PaymentManagement/DetailIncomingPayment')));
const DetailManullyMatching = Loadable(lazy(() => import('../pages/PaymentManagement/DetailManullyMatching')));
const SearchProblem = Loadable(lazy(() => import('../pages/PaymentManagement/SearchProblem')));
const DetailSearchProblem = Loadable(lazy(() => import('../pages/PaymentManagement/DetailSearchProblem')));
const LateCharge = Loadable(lazy(() => import('../pages/PaymentManagement/LateCharge')));
const LateChargeApprove = Loadable(lazy(() => import('../pages/PaymentManagement/LateChargeApprove')));
const LateChargeLastApprove = Loadable(lazy(() => import('../pages/PaymentManagement/LateChargeLastApprove')));
const DetailLateChargeCreate = Loadable(lazy(() => import('../pages/PaymentManagement/DetailLateChargeCreate')));
const DetailLateChargeOther = Loadable(lazy(() => import('../pages/PaymentManagement/DetailLateChargeOther')));

// Main
const LandingPage = Loadable(lazy(() => import('../pages/LandingPage')));
// Login Callback
const LoginRedirectCallback = Loadable(lazy(() => import('../pages/auth/LoginRedirectCallback')));
const LoginPopupCallback = Loadable(lazy(() => import('../pages/auth/LoginPopupCallback')));
const LoginSilentCallback = Loadable(lazy(() => import('../pages/auth/LoginSilentCallback')));
const LogoutFrontChannel = Loadable(lazy(() => import('../pages/auth/LogoutFrontChannel')));

const BillingReceipt = Loadable(lazy(() => import('../pages/DocumentManagement/BillingReceipt')));
const DocumentInvoice = Loadable(lazy(() => import('../pages/DocumentManagement/DocumentInvoice')));
const TicketBE = Loadable(lazy(() => import('../pages/DocumentManagement/TicketBE')));
const TicketDLC = Loadable(lazy(() => import('../pages/DocumentManagement/TicketDLC')));

// Report
const PlanCashInFlow = Loadable(lazy(() => import('../pages/Report/PlanCashInFlow')));
const OverdueReport = Loadable(lazy(() => import('../pages/Report/OverdueReport')));
const MonthlyReport = Loadable(lazy(() => import('../pages/Report/MonthlyReport')));
const ChequeReturnReport = Loadable(lazy(() => import('../pages/Report/ChequeReturnReport')));
const SalesVolumeByCreditTerm = Loadable(lazy(() => import('../pages/Report/SalesVolumeByCreditTerm')));
const KPIOverdue = Loadable(lazy(() => import('../pages/Report/KPIOverdue')));
const Committee = Loadable(lazy(() => import('../pages/Report/Committee')));
const Questionnaires = Loadable(lazy(() => import('../pages/Questionnaires')));
const NonSap = Loadable(lazy(() => import('../pages/Report/NonSap')));

// Export
const FollowingPaymentExport = Loadable(lazy(() => import('../pages/ExportPayment/FollowingPayment')));
const SearchProblemExport = Loadable(lazy(() => import('../pages/ExportPayment/ExportSearchProblem')));
const DetailSearchProblemExport = Loadable(lazy(() => import('../pages/ExportPayment/ExportDetailSearchProblem')));
const OverdueReportExport = Loadable(lazy(() => import('../pages/ExportPayment/ExportOverdueReport')));
const IncomingPaymentExport = Loadable(lazy(() => import('../pages/ExportPayment/IncomingPayment')));
const CreditAdvice = Loadable(lazy(() => import('../pages/ExportPayment/CreditAdvice')));
const IncomingExportPaymentManualDetail = Loadable(
  lazy(() => import('../pages/ExportPayment/IncomingExportPaymentManualDetail'))
);
const IncomingExportPaymentManualCreate = Loadable(
  lazy(() => import('../pages/ExportPayment/IncomingExportPaymentManualCreate'))
);
